import _ from 'lodash';

import {type BucketedQueryState} from './bucketedQueryManager';

export type BucketedRunsReducerState = {
  aggregatedByRunsetId: Record<string, boolean>;
  allRunsetsHaveBackendLines: boolean;
  backendLinesDomain: {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
  } | null;
  fetchedRunCount: number;
  /**
   * Per-runset run counts, mirroring `fetchedRunCount` but keyed by runsetId
   * so consumers can reason about the **per-runset** cap. The warning layer
   * uses `Math.max(...)` over this to detect "did any one runset hit its cap",
   * which is the correct signal for multi-runset report panels where
   * `fetchedRunCount` (a cross-runset sum) inflates past the per-runset cap
   * even when no individual runset has.
   */
  fetchedRunCountByRunsetId: Record<string, number>;
  hasSubsampledGroups: boolean;
  loadingByRunSetId: Record<string, boolean>;
  isLoading: boolean;
  runDataByRunsetId: Record<string, BucketedQueryState>;
  runNames: string[];
  totalGroupedIncludedRuns: number;
};
type LoadingAction = {
  type: 'bucketedRuns/setLoadingByRunSetId';
  runsetId: string;
  loading: boolean;
};
type ComboAction = {
  type: 'bucketedRuns/combo';
  data: BucketedQueryState;
  isAggregated: boolean;
  loading: boolean;
  runsetId: string;
};

type AggregatedAction = {
  type: 'bucketedRuns/setAggregatedByRunsetId';
  runsetId: string;
  isAggregated: boolean;
};
type BucketedRunsReducerAction = LoadingAction | AggregatedAction | ComboAction;

function handleSetLoadingByRunSetId(
  state: BucketedRunsReducerState,
  action: ComboAction | LoadingAction
) {
  const oldState = state.loadingByRunSetId[action.runsetId];
  /**
   * Important: `isLoading` starts as true to avoid flashing an error before any
   * data arrives. However, when we *reuse* an existing request, the first
   * handler call can legitimately come in with `loading: false` (cached data).
   * In that case `oldState` is undefined and `action.loading` is false, and if
   * we no-op we can get stuck in `isLoading: true` forever.
   */
  if (
    Boolean(oldState) === Boolean(action.loading) &&
    !(oldState === undefined && state.isLoading === true)
  ) {
    return state;
  }

  const newLoadingByRunSetId = {
    ...state.loadingByRunSetId,
    [action.runsetId]: action.loading,
  };
  return {
    ...state,
    loadingByRunSetId: newLoadingByRunSetId,
    isLoading: Object.values(newLoadingByRunSetId).some(Boolean),
  };
}

function handleSetRunNames(state: BucketedRunsReducerState) {
  // Build a stable, de-duplicated list of run names for runsInfoQuery.
  // Object iteration order across runsById can vary as deltas merge, and baseline/pinned
  // merging can amplify churn. Since the downstream filter semantics are set-based,
  // keep this list sorted to avoid infinite refetch loops and stuck loading.
  const namesSet = new Set<string>();
  for (const {data} of Object.values(state.runDataByRunsetId)) {
    for (const r of Object.values(data?.runsById ?? {})) {
      if (r?.name != null) {
        namesSet.add(r.name);
      }
    }
  }
  const updateNames = Array.from(namesSet).sort();

  if (!_.isEqual(state.runNames, updateNames)) {
    return {
      ...state,
      runNames: updateNames,
    };
  }

  return state;
}
// This is a critical reducer computation: making sure we keep reference equality here when polling updates but no data changes is critical for making sure the UI doesn't thrash and try to render all the panels. The output state here is what ultimately gets serialized down into the cache as well.
function handleSetRunDataByRunsetId(
  state: BucketedRunsReducerState,
  action: ComboAction
) {
  // this deep equals check is much, much cheaper than an extra render,
  // and much less costly than missing a subtle difference with a hand-rolled equality check,
  // so it's worth the price
  // Compare the entire data object (including order) not just runsById
  const hasChanged = !_.isEqual(
    state.runDataByRunsetId[action.runsetId]?.data,
    action.data?.data
  );

  if (hasChanged) {
    const updatedState = {
      ...state,
      runDataByRunsetId: {
        ...state.runDataByRunsetId,
        [action.runsetId]: action.data ?? {},
      },
    };
    return handleSetRunNames(updatedState);
  }
  return state;
}

/**
 * Derives the domain (min/max x and y values) from backend-constructed historyLines.
 * This is used when all runsets have backend lines, to compute bounds from
 * the available data.
 *
 * Returns null for backendLinesDomain if any runset does not have backend lines
 */
function deriveBackendLinesDomain(
  state: BucketedRunsReducerState
): BucketedRunsReducerState {
  // if it's not all backend lines, we can't trust the domain calculation
  if (!state.allRunsetsHaveBackendLines) {
    return state.backendLinesDomain === null
      ? state
      : {...state, backendLinesDomain: null};
  }

  const newDomain = {
    xMin: Infinity,
    xMax: -Infinity,
    yMin: Infinity,
    yMax: -Infinity,
  };

  // Iterate through all runsets and their runs to compute the domain
  for (const queryState of Object.values(state.runDataByRunsetId)) {
    const runs = Object.values(queryState.data?.runsById ?? {});
    for (const run of runs) {
      const historyLines = run?.historyLines ?? [];
      for (const line of historyLines) {
        if (line.minX < newDomain.xMin) {
          newDomain.xMin = line.minX;
        }
        if (line.maxX > newDomain.xMax) {
          newDomain.xMax = line.maxX;
        }
        if (line.minY < newDomain.yMin) {
          newDomain.yMin = line.minY;
        }
        if (line.maxY > newDomain.yMax) {
          newDomain.yMax = line.maxY;
        }
      }
    }
  }

  // If we can't set a valid domain, return null
  if (
    newDomain.xMin === Infinity ||
    newDomain.xMax === -Infinity ||
    newDomain.yMin === Infinity ||
    newDomain.yMax === -Infinity
  ) {
    return state.backendLinesDomain === null
      ? state
      : {...state, backendLinesDomain: null};
  }

  // Only update if the domain has changed
  if (_.isEqual(state.backendLinesDomain, newDomain)) {
    return state;
  }

  return {
    ...state,
    backendLinesDomain: newDomain,
  };
}

/**
 * Derives whether ALL runs across ALL runsets have backend-constructed historyLines.
 * This runs after handleSetRunDataByRunsetId to ensure we see the updated data.
 * Returns the same state reference if the derived value hasn't changed.
 */
function deriveAllRunsetsHaveBackendLines(
  state: BucketedRunsReducerState
): BucketedRunsReducerState {
  const runsetStates = Object.values(state.runDataByRunsetId);

  // Check if ALL runs across ALL runsets have historyLines.
  // Must have at least one runset with runs to return true.
  const allHaveBackendLines =
    runsetStates.length > 0 &&
    runsetStates.every(queryState => {
      const runs = Object.values(queryState.data?.runsById ?? {});
      return (
        runs.length > 0 &&
        runs.every(run => (run?.historyLines?.length ?? 0) > 0)
      );
    });

  if (state.allRunsetsHaveBackendLines !== allHaveBackendLines) {
    return {...state, allRunsetsHaveBackendLines: allHaveBackendLines};
  }
  return state;
}

/**
 * Derives whether any group across any runset had runs excluded from aggregation
 * due to the maxTotalRuns budget distribution. This runs after handleSetRunDataByRunsetId
 * to ensure we see the updated data.
 * Returns the same state reference if the derived value hasn't changed.
 */
function deriveHasSubsampledGroups(
  state: BucketedRunsReducerState
): BucketedRunsReducerState {
  let hasSubsampled = false;
  let totalIncludedRuns = 0;
  for (const queryState of Object.values(state.runDataByRunsetId)) {
    for (const run of Object.values(queryState.data?.runsById ?? {})) {
      if (run?.groupIncludedRuns != null) {
        totalIncludedRuns += run.groupIncludedRuns;
      }
      if (
        run?.groupTotalRuns != null &&
        run.groupTotalRuns > (run.groupIncludedRuns ?? 0)
      ) {
        hasSubsampled = true;
      }
    }
  }

  if (
    state.hasSubsampledGroups !== hasSubsampled ||
    state.totalGroupedIncludedRuns !== totalIncludedRuns
  ) {
    return {
      ...state,
      hasSubsampledGroups: hasSubsampled,
      totalGroupedIncludedRuns: totalIncludedRuns,
    };
  }
  return state;
}

/**
 * Derives both the cross-runset sum (`fetchedRunCount`) and per-runset counts
 * (`fetchedRunCountByRunsetId`) in a single pass over `runDataByRunsetId`.
 *
 * The sum remains the "total rows fetched" diagnostic and is intentionally
 * un-deduplicated across runsets — see the "double-counts" test in
 * bucketedDataReducer.test.ts. The per-runset map exists so the warning layer
 * can ask "did any individual runset hit its cap?", which is the correct
 * question for multi-runset report panels where the sum is polysemous.
 *
 * Returns the same state reference when **both** shape and values are
 * unchanged, to preserve React.memo / useSelector reference equality.
 */
function deriveFetchedRunCount(
  state: BucketedRunsReducerState
): BucketedRunsReducerState {
  let fetchedRunCount = 0;
  const fetchedRunCountByRunsetId: Record<string, number> = {};
  for (const [runsetId, queryState] of Object.entries(
    state.runDataByRunsetId
  )) {
    const count = Object.values(queryState.data?.runsById ?? {}).length;
    fetchedRunCountByRunsetId[runsetId] = count;
    fetchedRunCount += count;
  }

  // Nullish-coalesce guards against loosely-typed test fixtures that don't
  // include the new field; real call sites always initialize it to `{}`.
  const priorMap = state.fetchedRunCountByRunsetId ?? {};
  const mapChanged = !_.isEqual(priorMap, fetchedRunCountByRunsetId);
  const sumChanged = state.fetchedRunCount !== fetchedRunCount;
  if (!mapChanged && !sumChanged) {
    return state;
  }
  return {
    ...state,
    fetchedRunCount,
    fetchedRunCountByRunsetId: mapChanged
      ? fetchedRunCountByRunsetId
      : priorMap,
  };
}

function handleSetAggregatedByRunsetId(
  state: BucketedRunsReducerState,
  action: ComboAction | AggregatedAction
) {
  /**
   * To avoid unnecessary renders, we only want to update the isAggregated state when the new state is different from the old state when both states are coerced to booleans:
   * 1. old state: undefined, new state: false => no update
   * 2. old state: false, new state: false => no update
   * 3. old state: false, new state: true => update
   * 4. old state: true, new state: false => update
   * 5. old state: true, new state: true => no update
   * 6. old state: true, new state: undefined => update
   * etc...
   */

  const prevValue = !!state.aggregatedByRunsetId[action.runsetId];
  const newValue = !!action.isAggregated;
  if (prevValue !== newValue) {
    return {
      ...state,
      aggregatedByRunsetId: {
        ...state.aggregatedByRunsetId,
        [action.runsetId]: action.isAggregated,
      },
    };
  }
  return state;
}

export function bucketedRunsReducer(
  state: BucketedRunsReducerState,
  action: BucketedRunsReducerAction
) {
  switch (action.type) {
    case 'bucketedRuns/combo': {
      // the combo action is a bunch of discrete state transformations that we want to do in a single pass
      // this keeps the state from thrashing if multiple changes need to be batched so that we don't flicker
      // the UI. Writing it this way allowed me to keep each state transformation separate
      return [
        handleSetAggregatedByRunsetId,
        handleSetLoadingByRunSetId,
        handleSetRunDataByRunsetId,
        deriveHasSubsampledGroups, // must run after handleSetRunDataByRunsetId
        deriveFetchedRunCount, // must run after handleSetRunDataByRunsetId
        deriveAllRunsetsHaveBackendLines, // must run after handleSetRunDataByRunsetId
        deriveBackendLinesDomain, // must run after deriveAllRunsetsHaveBackendLines
      ].reduce((acc, handler) => {
        return handler(acc, action);
      }, state);
    }

    case 'bucketedRuns/setAggregatedByRunsetId': {
      return handleSetAggregatedByRunsetId(state, action);
    }

    case 'bucketedRuns/setLoadingByRunSetId': {
      return handleSetLoadingByRunSetId(state, action);
    }

    default: {
      return state;
    }
  }
}
